export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogLine {
  readonly level: LogLevel
  readonly scope: string
  readonly message: string
  readonly details: readonly unknown[]
}

/** Receives every line that passes the log level, after it's written to the browser console. */
export type LogSink = (line: LogLine) => void

export interface Logger {
  debug(message: string, ...details: unknown[]): void
  info(message: string, ...details: unknown[]): void
  warn(message: string, ...details: unknown[]): void
  error(message: string, ...details: unknown[]): void
}

const SEVERITY: Readonly<Record<LogLevel, number>> = { debug: 0, info: 1, warn: 2, error: 3 }

let minLevel: LogLevel = import.meta.env.DEV ? 'debug' : 'info'
let sink: LogSink | null = null

export function setLogLevel(level: LogLevel): void {
  minLevel = level
}

export function setLogSink(next: LogSink | null): void {
  sink = next
}

export function createLogger(scope: string): Logger {
  const write = (level: LogLevel, message: string, details: unknown[]): void => {
    if (SEVERITY[level] < SEVERITY[minLevel]) return
    console[level](`[${scope}] ${message}`, ...details)
    sink?.({ level, scope, message, details })
  }

  return {
    debug: (message, ...details) => {
      write('debug', message, details)
    },
    info: (message, ...details) => {
      write('info', message, details)
    },
    warn: (message, ...details) => {
      write('warn', message, details)
    },
    error: (message, ...details) => {
      write('error', message, details)
    },
  }
}
